'use client';

import { zodResolver } from '@hookform/resolvers/zod';
import { useForm } from 'react-hook-form';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/Card';
import { ContactUsSchema, ContactUs } from '@/lib/types/validations';
import { toast } from 'react-toastify';
import { useRouter } from 'next/navigation';
import { CreateContact } from '@/lib/API/Database/contactUs/mutations';
import { SendContactUsEmail } from '@/lib/API/Services/contactUs/contactUs';

import { Button } from '@/components/ui/Button';
import { Textarea } from '@/components/ui/Textarea';
import { Form, FormControl, FormField, FormItem, FormMessage } from '@/components/Form';
import { Input } from '@/components/ui/Input';
import { Icons } from '@/components/Icons';
import { usePageView } from '@/lib/hooks/usePageView';

const formFields = [
  { name: 'firstname', label: 'First Name', placeholder: 'First Name *' },
  { name: 'lastname', label: 'Last Name', placeholder: 'Last Name *' },
  { name: 'email', label: 'Email', placeholder: 'you@example.com *', type: 'email' },
  /*{ name: 'phone', label: 'Phone', placeholder: '123-456-7890', type: 'tel' },*/
  { name: 'company_name', label: 'Company Name *', placeholder: 'Company Name *' },
  /*{ name: 'company_website', label: 'Company Website', placeholder: 'Company website' },*/
  { name: 'message', label: 'Message *', placeholder: 'Type your message here...', isTextarea: true }
];

export default function ContactPage() {
  usePageView();
  const router = useRouter();
  const form = useForm<ContactUs>({
    resolver: zodResolver(ContactUsSchema),
    defaultValues: {
      firstname: '',
      lastname: '',
      email: '',
      phone: '',
      company_name: '',
      company_website: '',
      message: ''
    }
  });

  const {
    reset,
    formState: { isSubmitting }
  } = form;

  const onSubmit = async (values: ContactUs) => {
    const { firstname, lastname, email, phone, company_name, company_website, message } = values;
    const props = { firstname, lastname, email, phone, company_name, company_website, message };

    try {
      await CreateContact(props);
      await SendContactUsEmail(props);

      reset(); // Clear form fields after successful submission
      toast.success('Message Sent');
      router.refresh(); // Refresh to update state if necessary
    } catch (err) {
      toast.error('Failed to send message. Please try again.');
      throw err;
    }
  };

  return (
    <div>
      <Card className="bg-background-light dark:bg-background-dark">
        <CardHeader className="space-y-1">
          <CardTitle className="text-2xl max-w-3xl p-8">
            Ready to transform your supply chain? Let&apos;s talk.
          </CardTitle>
        </CardHeader>

        <CardContent>
          <Form {...form}>
            <form onSubmit={form.handleSubmit(onSubmit)}
className="space-y-8">
              <div className="flex space-x-4">
                {formFields.slice(0, 2).map((field) => (
                  <FormField
                    key={field.name}
                    control={form.control}
                    name={field.name as keyof ContactUs}
                    render={({ field: inputField }) => (
                      <FormItem className="w-1/2">
                        <FormControl>
                          <Input
                            type={field.type || 'text'}
                            placeholder={field.placeholder}
                            {...inputField}
                            className="bg-background-light dark:bg-background-dark"
                          />
                        </FormControl>
                        <FormMessage />
                      </FormItem>
                    )}
                  />
                ))}
              </div>

              {formFields.slice(2).map((field) => (
                <FormField
                  key={field.name}
                  control={form.control}
                  name={field.name as keyof ContactUs}
                  render={({ field: inputField }) => (
                    <FormItem>
                      <FormControl>
                        {field.isTextarea ? (
                          <Textarea
                            placeholder={field.placeholder}
                            {...inputField}
                            className="bg-background-light dark:bg-background-dark"
                          />
                        ) : (
                          <Input
                            type={field.type || 'text'}
                            placeholder={field.placeholder}
                            {...inputField}
                            className="bg-background-light dark:bg-background-dark"
                          />
                        )}
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />
              ))}

              <Button type="submit"
disabled={isSubmitting}
className="w-full bg-emerald-600 hover:bg-emerald-700">
                {isSubmitting && <Icons.Spinner className="mr-2 h-4 w-4 animate-spin" />}
                {isSubmitting ? 'Sending...' : 'Send Message'}
              </Button>
            </form>
          </Form>
        </CardContent>
      </Card>
    </div>
  );
}
